import { useContext, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Divider, Grid, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'

import SimpleTable from '../../../../components/simple-table'
import { AppContext, useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import useSharedStyles from '../../../../utils/shared.styles'

interface IProps {
  expandedDetails: IReturnItemExpanded
  orderNodeId: number
}

const missingItemsColumns = [
  {
    field: 'sku',
    flex: 1,
    headerName: 'SKU',
    sortable: false,
  },
  {
    field: 'iccid',
    flex: 1,
    headerName: 'ICCID',
    sortable: false,
  },
  {
    field: 'model',
    flex: 1,
    headerName: 'Model',
    sortable: false,
  },
  {
    field: 'sn',
    flex: 1,
    headerName: 'Serial Number',
    sortable: false,
  },
] satisfies GridColDef[]

const ReturnAccordionDetails = ({ expandedDetails, orderNodeId }: IProps) => {
  const auth = useAuth()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)
  const sharedClasses = useSharedStyles
  const [missingItems, setMissingItems] = useState<{ [key: string]: string }[]>([])
  const isScrap = expandedDetails.disposition === 'scrap'

  const fetchData = useEffectEvent(() => {
    if (!host || !auth.user?.access_token || !orderNodeId) return
    Api.get(`${host}/api/data/current?nodeId=${orderNodeId}`).then((res) => {
      if (res.ok) {
        const data = res.body[orderNodeId]
        if (data?.missing_items) {
          let parsedValue
          try {
            parsedValue = JSON.parse(data.missing_items.value)
            setMissingItems(parsedValue)
          } catch {
            // do nothing; bad format so we won't be displaying missing items
          }
        }
      }
    })
  })
  useEffect(() => {
    fetchData()
  }, [orderNodeId, host, pageRefreshCounter])

  const columns = useMemo(
    () =>
      [
        {
          field: 'uniqueId',
          headerName: isScrap ? 'Devices Being Scrapped' : 'Devices Being Returned',
          flex: 1,
          sortable: false,
        },
      ] satisfies GridColDef[],
    [isScrap]
  )

  const rows = useMemo(
    () =>
      expandedDetails?.devices?.map((d, idx) => ({
        id: idx,
        uniqueId: d.uniqueId,
      })),
    [expandedDetails.devices]
  )

  const missingItemsRows = useMemo(
    () =>
      missingItems?.map((item, idx) => ({
        ...item,
        id: idx,
      })),
    [missingItems]
  )

  return (
    <Grid
      container={true}
      sx={{
        gap: 2,
      }}
    >
      <Grid size={8.9}>
        <SimpleTable columns={columns} rows={rows} />
      </Grid>
      <Grid size="auto">
        <Divider orientation="vertical" variant="middle" />
      </Grid>
      <Grid
        size="grow"
        sx={{
          paddingTop: 1,
        }}
      >
        <Typography variant="overline" sx={sharedClasses.secondaryText}>
          {isScrap ? 'Reason + Notes' : 'Return Reason + Notes'}
        </Typography>
        <Typography variant="body1">{expandedDetails.returnReason}</Typography>
        <Typography variant="body1">{expandedDetails.returnNotes}</Typography>
      </Grid>
      {missingItems?.length > 0 && (
        <Grid size={8.9}>
          <Typography variant="h6">Missing Items</Typography>

          <SimpleTable columns={missingItemsColumns} rows={missingItemsRows} />
        </Grid>
      )}
    </Grid>
  )
}

export default ReturnAccordionDetails
